import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const EMOJI_MAP = { angry: '😠', sad: '😢', neutral: '😐', happy: '😊', excited: '🤩' };
const COLORS    = { angry: '#ef4444', sad: '#f97316', neutral: '#94a3b8', happy: '#22c55e', excited: '#8b5cf6' };
const STARS     = (score) => {
  const n = Math.round((parseFloat(score) / 5) * 5);
  return '★'.repeat(n) + '☆'.repeat(5 - n);
};

function SentimentMiniBar({ counts }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!total) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No data</span>;
  return (
    <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', width: 120, gap: 1 }}>
      {Object.entries(counts).map(([k, v]) =>
        v > 0 ? <div key={k} title={`${k}: ${v}`} style={{ flex: v, background: COLORS[k], minWidth: 2 }} /> : null
      )}
    </div>
  );
}

function StaffHistory({ staffId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/staff-performance/${staffId}/history`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [staffId]);

  if (loading) return <div style={{ padding: 20 }}><div className="spinner" /></div>;
  if (!data) return null;

  const { staff, responses } = data;

  return (
    <div className="card" style={{ marginTop: 12, borderLeft: '3px solid var(--primary)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
        <h4>{staff.name}'s feedback history</h4>
        <button className="btn btn-outline btn-sm" onClick={onClose}>✕ Close</button>
      </div>
      {responses.length === 0 ? (
        <p className="text-muted" style={{ fontSize: 13 }}>No feedbacks yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {responses.map(r => (
            <div key={r._id} style={{ background: 'var(--bg)', borderRadius: 8, padding: '9px 13px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16 }}>{r.feedbackType === 'audio' ? '🎙️' : '📋'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>
                  {r.feedbackType === 'audio' ? 'Voice' : 'Emoji'}
                  {r.billId && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>· Bill: {r.billId}</span>}
                  {r.customerName && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>· {r.customerName}</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(r.createdAt).toLocaleString()}</div>
              </div>
              {r.overallSentiment && (
                <span style={{ fontSize: 18 }}>{EMOJI_MAP[r.overallSentiment]}</span>
              )}
              {r.averageScore && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{parseFloat(r.averageScore).toFixed(1)}/5</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResetPasswordModal({ staff, onClose }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.put(`/staff-performance/${staff._id}/reset-password`, { newPassword: password });
      setSuccess('Password updated successfully!');
      setPassword('');
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 380, maxWidth: '90vw' }}>
        <h4 style={{ marginBottom: 4 }}>Reset Password</h4>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Set a new password for <strong>{staff.name}</strong></p>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <form onSubmit={handleReset}>
          <div className="form-group">
            <label>New Password</label>
            <input className="form-control" type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ flex: 1, justifyContent: 'center' }}>
              {loading ? 'Updating…' : 'Update Password'}
            </button>
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function StaffPerformanceTab() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [resetStaff, setResetStaff] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/staff-performance')
      .then(r => { setStaff(r.data.data); setLoading(false); })
      .catch(() => { setError('Failed to load'); setLoading(false); });
  }, []);

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="page-header">
        <h3>Staff Performance</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Feedback collected per staff member, their scores and sentiment breakdown.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {staff.length === 0 ? (
        <div className="card text-center" style={{ padding: 56 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
          <p className="text-muted">No staff added yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {staff.map((s, i) => (
            <div key={s._id}>
              <div className="card" style={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === s._id ? null : s._id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>

                  {/* Rank */}
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: i < 3 ? ['#fbbf24','#94a3b8','#d97706'][i] : 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: i < 3 ? '#fff' : 'var(--text-muted)', flexShrink: 0 }}>
                    {i + 1}
                  </div>

                  {/* Name & status */}
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.email}</div>
                  </div>

                  {/* Stats */}
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--primary)' }}>{s.totalResponses}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Responses</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 700, fontSize: 18 }}>{s.totalSessions}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Sessions</div>
                    </div>
                    {s.avgScore && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#f59e0b', letterSpacing: -1 }}>{STARS(s.avgScore)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{s.avgScore}/5 avg</div>
                      </div>
                    )}
                    {s.dominantSentiment && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 22 }}>{EMOJI_MAP[s.dominantSentiment]}</div>
                        <div style={{ fontSize: 10, color: COLORS[s.dominantSentiment], textTransform: 'capitalize' }}>{s.dominantSentiment}</div>
                      </div>
                    )}
                    <SentimentMiniBar counts={s.sentimentCounts} />
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                    {/*<button*/}
                    {/*  className="btn btn-outline btn-sm"*/}
                    {/*  onClick={() => setResetStaff(s)}*/}
                    {/*>*/}
                    {/*  🔑 Reset Password*/}
                    {/*</button>*/}
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
                      {expandedId === s._id ? '▲' : '▼'}
                    </span>
                  </div>
                </div>

                {/* Sentiment detail */}
                {s.totalResponses > 0 && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {Object.entries(s.sentimentCounts).map(([key, val]) => val > 0 && (
                      <span key={key} style={{ fontSize: 11, padding: '2px 9px', borderRadius: 10, background: COLORS[key] + '20', color: COLORS[key], fontWeight: 600 }}>
                        {EMOJI_MAP[key]} {key}: {val}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {expandedId === s._id && (
                <StaffHistory staffId={s._id} onClose={() => setExpandedId(null)} />
              )}
            </div>
          ))}
        </div>
      )}

      {/*{resetStaff && <ResetPasswordModal staff={resetStaff} onClose={() => setResetStaff(null)} />}*/}
    </div>
  );
}
